import React, { useState, useEffect } from 'react';
import UserLayout from '../components/UserLayout';
import { apiClient } from '../api/api';
import { Package, Send, Video, Search, Filter } from 'lucide-react';
import Loader from '../components/Loader';
import './AffiliatesPage.css';
import { useAuth } from '../context/AuthContext'; // Assuming useAuth is from AuthContext

const AffiliatesPage = () => {
  const [sampleFunnel, setSampleFunnel] = useState(null);
  const [affiliates, setAffiliates] = useState([]);
  const [productSkus, setProductSkus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { selectedShopId, dateRange } = useAuth();

  useEffect(() => {
    fetchAffiliatesData();
  }, [selectedShopId, dateRange]);

  const fetchAffiliatesData = async () => {
    try {
      setLoading(true);
      let url = `/affiliates-metrics?range=${dateRange}`;
      if (selectedShopId) {
        url += `&shop_id=${selectedShopId}`;
      }
      const response = await apiClient.get(url, {
        withCredentials: true,
        credentials: 'include'
      });
      console.log("Affiliates Data:", response.data); // Debugging
      
      setSampleFunnel(response.data.sample_funnel);
      setAffiliates(response.data.affiliates);
      setProductSkus(response.data.product_skus || []);
    } catch (error) {
      console.error('Error fetching affiliates data:', error);
      setError('Failed to load data. Please try refreshing the page.');
    } finally {
      setLoading(false);
    }
  };

  const filteredAffiliates = affiliates.filter(affiliate =>
    affiliate.creator.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <UserLayout>
      {error && (
        <div style={{background:'#2d1a1a',border:'1px solid #7f1d1d',color:'#fca5a5',padding:'12px 16px',borderRadius:'8px',margin:'16px 16px 0'}}>
          {error}
        </div>
      )}
      <div className="page-relative-wrapper">
        <div className="affiliates-page-container">
        
        {/* Sample Journey Funnel */}
        <div className="funnel-section">
          <div className="funnel-header">
            <h2 className="funnel-title">Sample Journey Funnel</h2>
            <div className="funnel-badges">
              {affiliates.length > 0 && (
                <div className="badge-active">
                  Top {affiliates.length} Affiliates
                </div>
              )}
            </div>
          </div>

          <div className="funnel-grid">
            {/* Card 1: Samples Requested */}
            {/* CHANGED (2026-11-03, flagged by Adriana): TikTok redesigned the Samples
                Analytics page and no longer exposes a raw count for this step anywhere --
                confirmed live (Key Metrics tiles on both tabs, every funnel step's hover
                tooltip, on a real Seller Center session). Was defaulting to 0 via `|| 0`,
                which looked like a real "zero requested" instead of "data unavailable".
                Show N/A with a note instead, per Roland's call, until TikTok exposes this
                figure again (if ever). */}
            <div
              className={`funnel-card requested ${loading ? 'shimmer skeleton' : ''}`}
              title={sampleFunnel && sampleFunnel.samples_requested == null ? 'TikTok no longer provides this figure on the Samples Analytics page.' : undefined}
            >
              <div className="card-icon-wrapper yellow">
                <Package size={24} />
              </div>
              <div className="card-value">{sampleFunnel?.samples_requested ?? 'N/A'}</div>
              <div className="card-label">Samples Requested</div>
            </div>

            {/* Card 2: Samples Sent */}
            <div className={`funnel-card sent ${loading ? 'shimmer skeleton' : ''}`}>
              <div className="card-icon-wrapper blue">
                <Send size={24} />
              </div>
              <div className="card-value">{sampleFunnel?.samples_sent ?? 0}</div>
              <div className="card-label">Samples Sent</div>
            </div>

            {/* Card 3: Posts Created */}
            <div className={`funnel-card created ${loading ? 'shimmer skeleton' : ''}`}>
              <div className="card-icon-wrapper green">
                <Video size={24} />
              </div>
              <div className="card-value">{sampleFunnel?.posts_created ?? 0}</div>
              <div className="card-label">Posts Created</div>
            </div>
          </div>

          <div className="funnel-metrics-row">
            <div className="metric-item">
              <div className="metric-label-small">Conversion Rate</div>
              <div className="metric-value-large">
                  {sampleFunnel?.conversion_rate || '0%'}
              </div>
            </div>
            <div className="metric-item">
              <div className="metric-label-small">Content Rate</div>
              <div className="metric-value-large green">
                {sampleFunnel?.content_rate || '0%'}
              </div>
            </div>
             {sampleFunnel?.avg_time_to_post && sampleFunnel.avg_time_to_post !== 'N/A' && sampleFunnel.avg_time_to_post !== '0' && sampleFunnel.avg_time_to_post !== '0 days' && (
              <div className="metric-item">
                <div className="metric-label-small">Avg. days to post</div>
                <div className="metric-value-large">
                  {sampleFunnel.avg_time_to_post.toString().replace(/ days/g, '')}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Product SKU Performance Table */}
        {productSkus.length > 0 && (
          <div className="table-section sku-table-override" style={{ marginBottom: '24px' }}>
            <div className="table-header">
              <h2 className="table-title">Product SKU Performance</h2>
              <div className="badge-active">{productSkus.length} SKUs</div>
            </div>
            <table className={`affiliates-table ${loading ? 'shimmer skeleton' : ''}`}>
              <thead>
                <tr>
                  <th>Product Name</th>
                  <th>Samples Requested</th>
                  <th>Samples Shipped</th>
                  <th>Content GMV</th>
                </tr>
              </thead>
              <tbody>
                {productSkus.map((sku, index) => (
                  <tr key={index}>
                    <td>
                      <div className="creator-cell" style={{ maxWidth: '320px' }}>
                        <div className="creator-details">
                          <span className="creator-name" style={{ whiteSpace: 'normal', lineHeight: '1.4' }}>
                            {sku.product_name?.length > 70 ? sku.product_name.substring(0, 70) + '...' : sku.product_name}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>{sku.samples_requested}</td>
                    <td>{sku.samples_shipped}</td>
                    <td>
                      <div className="gmv-cell">
                        <span>{sku.content_gmv}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Top Performing Affiliates */}
        <div className="table-section">
          <div className="table-header">
            <h2 className="table-title">Top Performing Affiliates</h2>
            <div className="table-controls">
              <div className="search-wrapper">
                <Search className="search-icon" />
                <input 
                  type="text" 
                  placeholder="Search creators..." 
                  className="search-input"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <button className="filter-btn" disabled title="Coming soon">
                <Filter size={16} /> Filter
              </button>
              <button className="clear-filter-btn" onClick={() => setSearchTerm('')}>
                Clear Filters
              </button>
            </div>
          </div>

          <table className={`affiliates-table ${loading ? 'shimmer skeleton' : ''}`}>
            <thead>
              <tr>
                <th>Creator</th>
                <th>Affiliate GMV</th>
                <th>Est. Commission</th>
                <th>AOV</th>
                <th>CTR</th>
                <th>Orders</th>
              </tr>
            </thead>
            <tbody>
              {filteredAffiliates.map((affiliate, index) => {
                 // Clean up currency strings/numbers
                 const parseCurrency = (val) => {
                     if (typeof val === 'number') return val;
                     if (typeof val === 'string' && val !== '--') return parseFloat(val.replace(/[^0-9.-]+/g,""));
                     return 0;
                 };

                 const gmv = parseCurrency(affiliate.affiliate_gmv);
                 const commission = parseCurrency(affiliate.est_commission);
                 
                 // Correctly map orders (API returns 'orders')
                 const orders = parseInt(affiliate.orders || affiliate.affiliate_orders || 0, 10);
                 
                 // Handle CTR -- means no data (show as-is, do not convert to 0%)
                 const ctr = affiliate.ctr && affiliate.ctr !== '--' ? affiliate.ctr : '--';
                 
                 // User requested AOV column, API returns 'aov' (avg order value)
                 const aov = affiliate.aov || '0';

                 return (
                  <tr key={index}>
                    <td>
                      <div className="creator-cell">
                        <div className="creator-avatar">
                          {affiliate.avatar ? (
                             <img src={affiliate.avatar} alt={affiliate.creator} referrerPolicy="no-referrer" />
                          ) : (
                             <div className="placeholder">{affiliate.creator ? affiliate.creator.charAt(0).toUpperCase() : '?'}</div>
                          )}
                        </div>
                        <div className="creator-details">
                          <span className="creator-name">{affiliate.creator ? affiliate.creator.replace('@','') : 'Unknown'}</span>
                          <span className="creator-handle">{affiliate.creator}</span>
                          <span className="creator-handle" style={{fontSize:'0.75rem', marginTop:'2px'}}>{affiliate.followers || affiliate.aff_followers || '0'} followers</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="gmv-cell">
                        <span>${isNaN(gmv) ? '0.00' : gmv.toFixed(2)}</span>
                        {/* <div className="gmv-sub">
                            <ArrowUpRight size={12} color="#10b981" /> 0%
                        </div> */}
                      </div>
                    </td>
                    <td>
                        <div className="commission-cell">
                            <span>${isNaN(commission) ? '0.00' : commission.toFixed(2)}</span>
                           {/* <span className="commission-sub">0%</span> */}
                        </div>
                    </td>
                    <td>${parseFloat(aov || 0).toFixed(2)}</td>
                    <td>{ctr}</td>
                    <td>{orders}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          
          <div className="pagination-footer">
             <span>Showing top {filteredAffiliates.length} affiliates by GMV</span>
          </div>
        </div>
        </div>
      </div>
    </UserLayout>
  );
};

export default AffiliatesPage;
